import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { flutterAdminApi } from './api';

export const Route = createFileRoute('/dashboard/feeds/')({
  component: FlutterEventsPage,
});

function FlutterEventsPage() {
  const navigate = useNavigate();
  const [selectedEventIds, setSelectedEventIds] = React.useState<string[]>([]);
  const [search, setSearch] = React.useState('');
  const [startDateFrom, setStartDateFrom] = React.useState('');

  const eventsQ = useQuery({
    queryKey: ['flutter-admin-events'],
    queryFn: flutterAdminApi.listEvents,
  });

  const toggleEvent = (providerEventId: string) => {
    setSelectedEventIds(current =>
      current.includes(providerEventId)
        ? current.filter(id => id !== providerEventId)
        : [...current, providerEventId]
    );
  };

  const handleBuild = async () => {
    if (!selectedEventIds.length) return;

    await navigate({
      to: '/dashboard/feeds/build',
      search: {
        eventIds: selectedEventIds.join(','),
        leagueCode: 'uk',
      },
    });
  };

  if (eventsQ.isLoading) {
    return <div className="p-12">Loading…</div>;
  }

  if (eventsQ.isError) {
    return <div className="p-12">Error: {(eventsQ.error as Error).message}</div>;
  }

  const events = eventsQ.data?.events ?? [];
  const normalizedSearch = search.trim().toLowerCase();
  const shouldShowResults = normalizedSearch.length >= 2 || Boolean(startDateFrom);

  const startDateFromUtc = startDateFrom ? new Date(`${startDateFrom}T00:00:00.000Z`) : null;

  const filteredEvents = shouldShowResults
    ? events.filter(event => {
        const matchesSearch =
          !normalizedSearch ||
          [event.name, event.competition ?? '', event.providerEventId, ...(event.brands ?? [])]
            .join(' ')
            .toLowerCase()
            .includes(normalizedSearch);

        const matchesDate =
          !startDateFromUtc ||
          (event.startUtc != null &&
            !Number.isNaN(new Date(event.startUtc).getTime()) &&
            new Date(event.startUtc) >= startDateFromUtc);

        return matchesSearch && matchesDate;
      })
    : [];

  const selectedEvents = events
    .filter(event => selectedEventIds.includes(event.providerEventId))
    .sort((a, b) => {
      const aTime = eventStartTime(a.startUtc);
      const bTime = eventStartTime(b.startUtc);

      if (aTime !== bTime) {
        return aTime - bTime;
      }

      return a.name.localeCompare(b.name);
    });

  const visibleResultIds = new Set(filteredEvents.map(event => event.providerEventId));

  const selectVisible = () => {
    setSelectedEventIds(current => {
      const next = new Set(current);

      filteredEvents.forEach(event => {
        next.add(event.providerEventId);
      });

      return Array.from(next);
    });
  };

  const clearVisible = () => {
    setSelectedEventIds(current => current.filter(id => !visibleResultIds.has(id)));
  };

  const clearAllSelected = () => {
    setSelectedEventIds([]);
  };

  const clearFilters = () => {
    setSearch('');
    setStartDateFrom('');
  };

  return (
    <div className="space-y-6 p-6 lg:p-12">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Pickem Builder</h1>
          <p className="text-sm text-muted-foreground">
            Search for fixtures, select the ones you want, then build your game.
          </p>
        </div>

        <button
          type="button"
          onClick={handleBuild}
          disabled={!selectedEventIds.length}
          className="rounded-md border px-4 py-2 disabled:opacity-50"
        >
          Build game ({selectedEventIds.length})
        </button>
      </div>

      <div className="rounded-2xl border p-4 space-y-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px_auto]">
          <div>
            <label className="block text-sm font-medium">Search fixtures</label>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by event, competition, event ID, or brand"
              className="mt-1 w-full rounded-md border px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Start date from</label>
            <input
              type="date"
              value={startDateFrom}
              onChange={e => setStartDateFrom(e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2"
            />
          </div>

          <div className="flex items-end">
            <button
              type="button"
              onClick={clearFilters}
              disabled={!search && !startDateFrom}
              className="w-full rounded-md border px-3 py-2 text-sm disabled:opacity-50 lg:w-auto"
            >
              Clear filters
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>{selectedEventIds.length} selected</span>

          {shouldShowResults ? (
            <span>
              • {filteredEvents.length} result{filteredEvents.length === 1 ? '' : 's'}
            </span>
          ) : null}

          {selectedEventIds.length > 0 ? (
            <button
              type="button"
              onClick={clearAllSelected}
              className="ml-auto rounded-md border px-3 py-1.5 text-sm"
            >
              Clear selected
            </button>
          ) : null}
        </div>

        <p className="text-xs text-muted-foreground">
          Enter at least 2 characters to search, or choose a start date to browse upcoming fixtures
          from that date.
        </p>
      </div>

      {selectedEvents.length > 0 ? (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold">Selected fixtures</h2>
            <p className="text-sm text-muted-foreground">
              These will be included when you continue to the builder.
            </p>
          </div>

          <div className="space-y-3">
            {selectedEvents.map(event => (
              <label
                key={event.providerEventId}
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-foreground bg-muted/30 p-4"
              >
                <input
                  type="checkbox"
                  checked
                  onChange={() => toggleEvent(event.providerEventId)}
                  className="mt-1"
                />

                <div className="min-w-0 flex-1">
                  <div className="font-medium">{event.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {event.competition ?? 'No competition'}
                    {event.startUtc ? ` • ${formatUtc(event.startUtc)}` : ''}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Event ID: {event.providerEventId}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {event.brands.map(brand => (
                      <span
                        key={`${event.providerEventId}-${brand}`}
                        className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground"
                      >
                        {brand}
                      </span>
                    ))}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </section>
      ) : null}

      <section className="space-y-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold">Search results</h2>
            <p className="text-sm text-muted-foreground">
              Find fixtures by name, competition, event ID, brand, or starting date.
            </p>
          </div>

          {shouldShowResults && filteredEvents.length > 0 ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectVisible}
                className="rounded-md border px-3 py-1.5 text-sm"
              >
                Select results
              </button>

              <button
                type="button"
                onClick={clearVisible}
                className="rounded-md border px-3 py-1.5 text-sm"
              >
                Clear results
              </button>
            </div>
          ) : null}
        </div>

        {!shouldShowResults ? (
          <div className="rounded-xl border p-6 text-sm text-muted-foreground">
            Start typing to search fixtures, or choose a start date.
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="rounded-xl border p-6 text-sm text-muted-foreground">
            No fixtures match your filters.
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEvents.map(event => {
              const checked = selectedEventIds.includes(event.providerEventId);

              return (
                <label
                  key={event.providerEventId}
                  className={[
                    'flex cursor-pointer items-start gap-3 rounded-xl border p-4',
                    checked ? 'border-foreground bg-muted/30' : '',
                  ].join(' ')}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleEvent(event.providerEventId)}
                    className="mt-1"
                  />

                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{event.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {event.competition ?? 'No competition'}
                      {event.startUtc ? ` • ${formatUtc(event.startUtc)}` : ''}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Event ID: {event.providerEventId}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {event.brands.map(brand => (
                        <span
                          key={`${event.providerEventId}-${brand}`}
                          className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {brand}
                        </span>
                      ))}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function eventStartTime(value?: string | null) {
  if (!value) {
    return Number.MAX_SAFE_INTEGER;
  }

  const time = new Date(value).getTime();
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
}

function formatUtc(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return (
    new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC',
    }).format(date) + ' UTC'
  );
}
